import { Fragment } from 'react'
import { TableCell, TableHead } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { formatConversions, formatCount, formatMicros, formatRate } from '@/lib/adFormat'
import {
  adChange,
  clickThroughRate,
  costPerClickMicros,
  costPerConversionMicros,
  emptyAdTotals,
  type AdChange,
  type AdTotals,
} from '@lib/helpers/ad-numbers'
import type { AdComparedDto } from '@lib/dto/ad-numbers.dto'

interface Metric {
  label: string
  /** What the header's hover says, where the label alone could mislead. */
  hint?: string
  value: (totals: AdTotals) => number | null
  format: (n: number, currencyCode: string) => string
  /** A rate's change is in percentage points, not in its own unit. */
  rate?: boolean
}

/**
 * The numbers every Ads table shows, in order. Every rate is worked out from
 * totals by the lib helpers, never averaged (docs/ad-analytics.spec.md).
 */
const METRICS: Metric[] = [
  { label: 'Spend', value: (t) => t.spendMicros, format: formatMicros },
  { label: 'Impr.', hint: 'Impressions: how often the ads were shown.', value: (t) => t.impressions, format: (n) => formatCount(n) },
  { label: 'Clicks', value: (t) => t.clicks, format: (n) => formatCount(n) },
  { label: 'CTR', hint: 'Click-through rate: clicks for every impression.', value: clickThroughRate, format: (n) => formatRate(n), rate: true },
  { label: 'Avg. CPC', hint: 'Average cost per click.', value: costPerClickMicros, format: formatMicros },
  {
    label: 'Conv.',
    hint: 'Conversions as Google counts them: the actions each account marks as conversions — not leads or bookings.',
    value: (t) => t.conversions,
    format: (n) => formatConversions(n),
  },
  { label: 'Cost / conv.', hint: 'Cost per conversion, as Google counts conversions.', value: costPerConversionMicros, format: formatMicros },
]

/** How a number moved, in words: `+$270.00 · +15%`, `new · $310.00`, or a dash. */
function changeText(change: AdChange, metric: Metric, currencyCode: string): string {
  if (change.kind === 'none') return '—'
  const amount = (n: number) => (metric.rate === true ? `${(n * 100).toFixed(2)} pts` : metric.format(n, currencyCode))
  if (change.kind === 'new') return `new · ${amount(change.amount)}`
  let sign = '±'
  if (change.amount > 0) sign = '+'
  else if (change.amount < 0) sign = '−'
  const percent = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 0 }).format(Math.abs(change.percent))
  // A move that rounds to nothing reads as nothing, rather than "−0%".
  const percentText = Math.abs(change.percent) < 0.005 ? '0%' : `${sign}${percent}`
  return `${sign}${amount(Math.abs(change.amount))} · ${percentText}`
}

/** Each currency present on either side, in a fixed order: normally just one. */
function currencies(numbers: AdComparedDto): string[] {
  return [...new Set([...numbers.current, ...numbers.earlier].map((t) => t.currencyCode))].sort()
}

/** The headers for {@link AdNumberCells}, in the same order. */
export function AdNumberHeads() {
  return (
    <>
      {METRICS.map((metric) => (
        <TableHead key={metric.label} className="whitespace-nowrap px-2.5 text-right">
          {metric.hint === undefined ? (
            metric.label
          ) : (
            <Tooltip content={metric.hint}>
              <span className="cursor-default">{metric.label}</span>
            </Tooltip>
          )}
        </TableHead>
      ))}
    </>
  )
}

interface AdNumberCellsProps {
  numbers: AdComparedDto
  /** Hide the change line, for a row whose comparison would mean nothing. */
  withoutChange?: boolean
  className?: string
}

/**
 * A row's numbers, one cell per metric: the value, and under it how it moved
 * from the period before. With no numbers at all, a dash.
 */
export function AdNumberCells({ numbers, withoutChange = false, className }: AdNumberCellsProps) {
  const codes = currencies(numbers)
  return (
    <>
      {METRICS.map((metric) => (
        <TableCell key={metric.label} className={cn('whitespace-nowrap px-2.5 text-right tabular-nums', className)}>
          {codes.length === 0 ? <span className="text-ink-muted">—</span> : null}
          {codes.map((code) => {
            const current = numbers.current.find((t) => t.currencyCode === code) ?? emptyAdTotals(code)
            const earlier = numbers.earlier.find((t) => t.currencyCode === code) ?? emptyAdTotals(code)
            const value = metric.value(current)
            return (
              <Fragment key={code}>
                <div className="text-ink">{value === null ? '—' : metric.format(value, code)}</div>
                {withoutChange ? null : (
                  <div className="text-xs text-ink-muted">{changeText(adChange(value, metric.value(earlier)), metric, code)}</div>
                )}
              </Fragment>
            )
          })}
        </TableCell>
      ))}
    </>
  )
}
